import React from 'react';
import styled from 'styled-components';
import { MarginTopProps } from 'styled-system';

import { Box } from 'components/Box';
import { Divider } from 'components/Divider';
import {
  CheckboxField,
  CreditCardField,
  CurrencyInputField,
  FormContainer as Form,
  FormSubmitFunction,
  GeneralError,
  HiddenField,
  LegacyInputField,
  RadioButtonsField,
  RecaptchaField,
  SubmitButton,
} from 'components/Form';
import { AddressWithoutLocationForm } from 'components/Form/AddressWithoutLocationForm';
import { Icon } from 'components/Icon';
import { H3 } from 'components/Text';
import { gutterWidth, transition } from 'theme';
import { trackClicked } from 'utils/analytics';

const AMOUNTS = [
  {
    label: '$25',
    value: 25,
  },
  {
    label: '$50',
    value: 50,
  },
  {
    label: '$100',
    value: 100,
  },
  {
    label: getText('Other Amount'),
    value: 'OTHER',
  },
];
const StyledSubmitButton = styled(SubmitButton)`
  svg {
    transition: transform ${transition};
  }
  &:hover,
  &:focus {
    svg {
      transform: translateX(50%);
    }
  }
`;

function StyledDivider({ mt }: { mt?: MarginTopProps['mt'] }) {
  return (
    <Divider light dashed my={[gutterWidth / 2, null, gutterWidth]} mt={mt} />
  );
}

type DonateBlockFormValues = {
  amount?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  country: string | null;
  isForHonoree?: boolean;
};

type Props = {
  onSubmit: FormSubmitFunction<DonateBlockFormValues>;
  campaignId: string;
  initialValues: Record<string, unknown>;
  loggedIn: boolean;
};

export function DonateBlockInternalForm({
  onSubmit,
  campaignId,
  initialValues,
  loggedIn,
}: Props) {
  return (
    <Form
      data-qa-id="donate-form"
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ getValue }) => (
        <>
          <GeneralError />
          <H3>{getText('Your Donation')}</H3>
          {/* @ts-expect-error ts-migrate(2322) FIXME: Type '{ name: string; value: string; }' is not ass... Remove this comment to see the full error message */}
          <HiddenField name="campaignId" value={campaignId} />
          <RadioButtonsField
            buttons={AMOUNTS}
            name="amount"
            label={getText('How much would you like to donate?')}
            onUpdateValue={(v) =>
              trackClicked('Preset Donation Amount', {
                amount: v,
                campaign_id: campaignId,
              })
            }
            square
          />
          {getValue('amount') === 'OTHER' ? (
            <CurrencyInputField
              name="otherAmount"
              min={5}
              errorMessage={getText('Please enter an amount of $5 or more')}
            />
          ) : null}
          <StyledDivider />
          <H3>{getText('Payment Info')}</H3>
          <CreditCardField wrap />
          <StyledDivider />
          <H3>{getText('Billing Info')}</H3>
          <LegacyInputField
            name="firstName"
            label={getText('First Name')}
            placeholder={getText('Casey')}
          />
          <LegacyInputField
            name="lastName"
            label={getText('Last Name')}
            placeholder={getText('Smith')}
          />
          <AddressWithoutLocationForm
            useUSStateDropdown={getValue('country') === 'US'}
          />
          <StyledDivider
            mt={[`${gutterWidth / 2 - 16}px`, null, gutterWidth - 30]}
          />
          <H3>{getText('Contact Info')}</H3>
          <LegacyInputField
            type="email"
            name="email"
            placeholder={getText('casey.smith@example.com')}
            label={getText('Email')}
          />
          <LegacyInputField
            name="phone"
            placeholder={getText('e.g. 123 555-1234')}
            label={getText('Phone Number (optional)')}
          />
          <StyledDivider />
          <CheckboxField
            name="isForHonoree"
            label={getText(
              'Dedicate this gift in honor or in memory of someone'
            )}
          />
          {getValue('isForHonoree') ? (
            <>
              <H3>{getText('Who is this gift dedicated to?')}</H3>
              <LegacyInputField
                name="honoreeFirstName"
                label={`${getText(`First Name`)}*`}
                placeholder={getText('Casey')}
              />
              <LegacyInputField
                name="honoreeLastName"
                label={`${getText(`Last Name`)}*`}
                placeholder={getText('Smith')}
              />
              <LegacyInputField
                name="honoreeEmail"
                type="email"
                label={getText('Email for E-card (optional)')}
                description={getText(
                  'An e-card will be sent to the email address provided'
                )}
                placeholder={getText('casey.smith@example.com')}
              />
              <StyledDivider />
            </>
          ) : null}

          {loggedIn ? null : <RecaptchaField />}

          <StyledSubmitButton
            mt={[gutterWidth / 2, null, gutterWidth]}
            p="15px 0"
            width={1}
            size="large"
          >
            {getText('Donate')}
            <Box display="inline-block" ml={12}>
              <Icon
                initialColorName="todo-replace-me"
                name="arrow-right"
                size={18}
              />
            </Box>
          </StyledSubmitButton>
        </>
      )}
    />
  );
}
